import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@c2fo/liquidity';
import FilterDrawer from '@/enterprise/components/FilterDrawer';
import { InvoiceAdjustmentFetchFilters, InvoiceAdjustmentFetchOptions } from '@/enterprise/data/useInvoiceAdjustments';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useLocaleFormat from '@/utils/useLocaleFormat';

const InvoiceAdjustmentFilters = ({
  filterOpen,
  handleFilter,
  setFilterOpen,
  takerMarket,
  urlState,
}: {
  filterOpen: boolean;
  handleFilter: ({ key, value }: { key: keyof InvoiceAdjustmentFetchFilters; value?: string }) => void;
  setFilterOpen: (open: boolean) => void;
  takerMarket: TakerMarket;
  urlState: Partial<InvoiceAdjustmentFetchOptions>;
}) => {
  const { t } = useTranslation();
  const { asCurrency } = useLocaleFormat();

  return (
    <FilterDrawer filterOpen={filterOpen} setFilterOpen={setFilterOpen}>
      <div className="space-y-4 p-4">
        <div className="w-full space-y-1">
          <Label>{t('core.status')}</Label>
          <Select
            onValueChange={(value) => {
              const filterValue = value === 'any' ? undefined : value === 'matched' ? 'matched' : 'unmatched';
              return handleFilter({ key: 'status', value: filterValue });
            }}
            value={
              urlState.filter?.status?.[0] === 'matched'
                ? 'matched'
                : urlState.filter?.status?.[0] === 'unmatched'
                ? 'unmatched'
                : 'any'
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">{t('core.any')}</SelectItem>
              <SelectItem value="matched">{t('taker.invoices.adjustment.statusMatched')}</SelectItem>
              <SelectItem value="unmatched">{t('taker.invoices.adjustment.statusUnmatched')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-full space-y-1">
          <Label>{t('core.adjustmentAmount')}</Label>
          <div className="space-y-2 rounded border border-stroke bg-white p-4">
            <Checkbox
              checked={(urlState?.filter?.amount ?? []).includes('-25000,')}
              color="secondary"
              id="-25000,"
              label={t('core.rangeFilterLowest', { max: `(${asCurrency(25000, takerMarket.currency)})` })}
              onChange={() => handleFilter({ key: 'amount', value: '-25000,' })}
              value="-25000,"
            />
            <Checkbox
              checked={(urlState?.filter?.amount ?? []).includes('-50000,-25000')}
              color="secondary"
              id="-50000,-25000"
              label={t('core.rangeFilterMiddle', {
                min: `(${asCurrency(25000, takerMarket.currency)})`,
                max: `(${asCurrency(50000, takerMarket.currency)})`,
              })}
              onChange={() => handleFilter({ key: 'amount', value: '-50000,-25000' })}
              value="-50000,-25000"
            />
            <Checkbox
              checked={(urlState?.filter?.amount ?? []).includes('-75000,-50000')}
              color="secondary"
              id="-75000,-50000"
              label={t('core.rangeFilterMiddle', {
                min: `(${asCurrency(50000, takerMarket.currency)})`,
                max: `(${asCurrency(75000, takerMarket.currency)})`,
              })}
              onChange={() => handleFilter({ key: 'amount', value: '-75000,-50000' })}
              value="-75000,-50000"
            />
            <Checkbox
              checked={(urlState?.filter?.amount ?? []).includes(',-75000')}
              color="secondary"
              id=",-75000"
              label={t('core.rangeFilterHighest', { min: `(${asCurrency(75000, takerMarket.currency)})` })}
              onChange={() => handleFilter({ key: 'amount', value: ',-75000' })}
              value=",-75000"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end px-4 pb-4">
        <Button onClick={() => setFilterOpen(false)} variant="secondary">
          {t('core.close')}
        </Button>
      </div>
    </FilterDrawer>
  );
};

export default InvoiceAdjustmentFilters;
